import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRight, Event, OpenInNew, ContentCopy } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { useGetProviderEncountersQuery, useGetProviderInfoQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function EncountersListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 1;
  });
  const [perPage, setPerPage] = useState(() => {
    const perPageParam = searchParams.get('perPage');
    return perPageParam ? parseInt(perPageParam, 10) : 20;
  });
  const [filters, setFilters] = useState<Record<string, string>>(() => {
    const initialFilters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key !== 'page' && key !== 'perPage') {
        initialFilters[key] = value;
      }
    });
    return initialFilters;
  });

  // Update URL when state changes
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Add page if not 1
    if (page !== 1) {
      newParams.set('page', page.toString());
    }

    // Add perPage if not 20
    if (perPage !== 20) {
      newParams.set('perPage', perPage.toString());
    }

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [page, perPage, filters, setSearchParams]);

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Fetch provider info from RTK Query
  const { data: provider, isLoading: userLoading } = useGetProviderInfoQuery(
    { providerId: providerId ?? '' },
    { skip: !providerId },
  );

  // Status badge color function
  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Get URL for each row (for shareable links)
  const getRowUrl = useCallback((encounter: any) => {
    return `/admin-v2/encounter/${encounter.encounter_id}`;
  }, []);

  // Get state for each row (not needed for shareable URLs)
  // const getRowState = useCallback(() => {
  //   return {};
  // }, []);

  // Fetch provider's encounters with pagination and filtering
  const { data: encountersData, isLoading: encountersLoading } = useGetProviderEncountersQuery(
    {
      providerId: providerId ?? '',
      page,
      perPage,
      ...filters,
    },
    {
      skip: !providerId,
    },
  );

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">Unable to load provider details.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const encounterColumns = [
    {
      key: 'encounter_id' as const,
      label: 'Encounter ID',
      render: (value: any) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm break-all text-blue-600">{String(value ?? '')}</span>
          {value && (
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  void navigator.clipboard.writeText(String(value));
                }}
              >
                <ContentCopy fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: 'created_at' as const,
      label: 'Created',
      render: (value: any) => formatDateTime(value),
    },
    {
      key: 'status' as const,
      label: 'Status',
      render: (value: any) => (
        <Badge color={getStatusBadgeColor(value)}>
          {value ? value.replace('_', ' ') : 'Unknown'}
        </Badge>
      ),
    },
    {
      key: 'encounter_type' as const,
      label: 'Type',
      render: (value: any) => <span className="text-sm text-gray-600">{value || 'N/A'}</span>,
    },
    {
      key: 'input_documents' as const,
      label: 'Input Docs',
      render: (value: any) => (
        <span className={`text-sm font-medium ${value === 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {value || 0}
        </span>
      ),
    },
    {
      key: 'note_id' as const,
      label: 'Note',
      render: (value: any) =>
        value ? (
          <span className="text-sm text-green-600">Yes</span>
        ) : (
          <span className="text-sm text-gray-400">No</span>
        ),
    },
  ] as const;

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'pending', label: 'Pending' },
        { value: 'failed', label: 'Failed' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      key: 'created_after',
      label: 'Created After',
      type: 'text' as const,
    },
    {
      key: 'created_before',
      label: 'Created Before',
      type: 'text' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center mb-6 text-sm">
          <Link to="/admin-v2">
            <Button
              variant="text"
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Admin
            </Button>
          </Link>
          <ChevronRight className="mx-1 h-4 w-4 text-gray-400" />
          <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
            <Button
              variant="text"
              className="text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              {provider?.name || 'Provider'}
            </Button>
          </Link>
          <ChevronRight className="mx-1 h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-900">Encounters</span>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Event className="mr-2 h-6 w-6" />
                Encounters
              </h1>
              <p className="text-sm text-gray-600 mt-1">Viewing encounters for {provider?.name}</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                <Button variant="outlined" size="small">
                  Back to Provider
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <FilterableDataTable
          title="Encounters"
          columns={encounterColumns as any}
          data={encountersData?.items || []}
          total={encountersData?.total}
          loading={encountersLoading}
          page={page}
          perPage={perPage}
          filters={filterConfigs}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onFiltersChange={handleFiltersChange}
          getRowUrl={getRowUrl}
          emptyMessage="No encounters to display"
        />
      </main>
    </div>
  );
}
